import { Injectable } from '@angular/core';
import { LanguageService } from './language.service';

export type TangoSymbol = 'sun' | 'moon' | null;
export type TangoGrid = TangoSymbol[][];
export type Constraint = 'equal' | 'not-equal' | null;

export interface TangoPuzzle {
    puzzle: TangoGrid;
    solution: ('sun' | 'moon')[][];
    hConstraints: Constraint[][]; // horizontal signs between (r, c) and (r, c+1)
    vConstraints: Constraint[][]; // vertical signs between (r, c) and (r+1, c)
}

@Injectable({
    providedIn: 'root'
})
export class TangoService {

    constructor(private langService: LanguageService) { }

    generatePuzzle(size: number, difficulty: 'easy' | 'medium' | 'hard' = 'medium'): TangoPuzzle {
        let puzzle: TangoGrid, solution: ('sun' | 'moon')[][], hConstraints: Constraint[][], vConstraints: Constraint[][];
        let attempts = 0;

        do {
            attempts++;
            solution = this.createEmptyGrid(size) as ('sun' | 'moon')[][];
            this.solveTango(solution, size);

            puzzle = solution.map(row => [...row]) as TangoGrid;

            // Define strict limits based on user feedback
            const limits = {
                easy: { startSymbolPercent: 0.45, maxSymbolPercent: 0.50, maxSigns: 9 },
                medium: { startSymbolPercent: 0.30, maxSymbolPercent: 0.40, maxSigns: 7 },
                hard: { startSymbolPercent: 0.15, maxSymbolPercent: 0.20, maxSigns: 5 }
            };
            const cfg = limits[difficulty];

            // Step 1: Initial symbol reveal (START value)
            const totalCells = size * size;
            const initialClueCount = Math.floor(totalCells * cfg.startSymbolPercent);
            const cellsToRemoveCount = totalCells - initialClueCount;
            this.removeCells(puzzle, cellsToRemoveCount, size);

            // Adjacency constraints start at ZERO
            hConstraints = Array.from({ length: size }, () => Array(size - 1).fill(null));
            vConstraints = Array.from({ length: size - 1 }, () => Array(size).fill(null));

            // Step 2: Incremental Solvability Check (Symbols then Signs)
            this.ensureStrictSolvability(puzzle, solution, size, hConstraints, vConstraints, cfg);

        } while (!this.hasUniqueSolution(puzzle, size, hConstraints, vConstraints) && attempts < 20);

        if (attempts >= 20) console.warn("Could not generate unique puzzle in 20 attempts");

        return { puzzle, solution, hConstraints, vConstraints };
    }

    private ensureStrictSolvability(grid: TangoGrid, sol: ('sun' | 'moon')[][], size: number, hC: Constraint[][], vC: Constraint[][], cfg: { maxSymbolPercent: number, maxSigns: number }) {
        const total = size * size;
        const maxSymbolClues = Math.floor(total * cfg.maxSymbolPercent);

        // 1. Add symbols up to Max percentage if not solvable
        while (!this.isSolvableByLogic(grid, sol, size, hC, vC)) {
            let currentClues = 0;
            const emptyCells: { r: number, c: number }[] = [];
            for (let r = 0; r < size; r++) {
                for (let c = 0; c < size; c++) {
                    if (grid[r][c] !== null) currentClues++;
                    else emptyCells.push({ r, c });
                }
            }

            if (currentClues >= maxSymbolClues || emptyCells.length === 0) break;

            const pick = emptyCells[Math.floor(Math.random() * emptyCells.length)];
            grid[pick.r][pick.c] = sol[pick.r][pick.c];
        }

        // 2. Add signs up to Max signs if still not solvable
        while (!this.isSolvableByLogic(grid, sol, size, hC, vC)) {
            if (this.countSigns(hC, vC) >= cfg.maxSigns) break;

            const added = this.tryAddStrategicConstraint(grid, sol, size, hC, vC);
            if (!added) break; // No more possible strategic signs
        }
    }

    private hasUniqueSolution(puzzle: TangoGrid, size: number, hC: Constraint[][], vC: Constraint[][]): boolean {
        let solutions = 0;
        const solve = (r: number, c: number) => {
            if (solutions > 1) return;
            if (r === size) {
                solutions++;
                return;
            }

            const nextR = c === size - 1 ? r + 1 : r;
            const nextC = c === size - 1 ? 0 : c + 1;

            if (puzzle[r][c] !== null) {
                solve(nextR, nextC);
                return;
            }

            for (const sym of ['sun', 'moon'] as const) {
                // Determine 'puzzle-so-far' temporarily
                puzzle[r][c] = sym;
                if (this.isPlacementValid(puzzle, r, c, sym, size, hC, vC)) {
                    solve(nextR, nextC);
                }
                puzzle[r][c] = null;
            }
        };

        const clone = puzzle.map(r => [...r]) as TangoGrid;
        solve(0, 0);
        return solutions === 1;
    }

    private isPlacementValid(grid: TangoGrid, row: number, col: number, sym: 'sun' | 'moon', size: number, hC: Constraint[][], vC: Constraint[][]): boolean {
        // 1. Basic Rules (3-in-row, Parity)
        if (!this.isValid(grid, row, col, sym, size)) return false;

        // 2. Connector Constraints
        // Horizontal (left)
        if (col > 0 && hC[row][col - 1] && grid[row][col - 1] !== null) {
            const s = hC[row][col - 1];
            if (s === 'equal' && grid[row][col - 1] !== sym) return false;
            if (s === 'not-equal' && grid[row][col - 1] === sym) return false;
        }
        // Horizontal (right - only if filled)
        if (col < size - 1 && hC[row][col] && grid[row][col + 1] !== null) {
            const s = hC[row][col];
            if (s === 'equal' && grid[row][col + 1] !== sym) return false;
            if (s === 'not-equal' && grid[row][col + 1] === sym) return false;
        }
        // Vertical (top)
        if (row > 0 && vC[row - 1][col] && grid[row - 1][col] !== null) {
            const s = vC[row - 1][col];
            if (s === 'equal' && grid[row - 1][col] !== sym) return false;
            if (s === 'not-equal' && grid[row - 1][col] === sym) return false;
        }
        // Vertical (bottom - only if filled)
        if (row < size - 1 && vC[row][col] && grid[row + 1][col] !== null) {
            const s = vC[row][col];
            if (s === 'equal' && grid[row + 1][col] !== sym) return false;
            if (s === 'not-equal' && grid[row + 1][col] === sym) return false;
        }

        return true;
    }
    private countSigns(hC: Constraint[][], vC: Constraint[][]): number {
        let count = 0;
        hC.forEach(row => row.forEach(c => { if (c) count++; }));
        vC.forEach(row => row.forEach(c => { if (c) count++; }));
        return count;
    }

    private tryAddStrategicConstraint(grid: TangoGrid, sol: ('sun' | 'moon')[][], size: number, hC: Constraint[][], vC: Constraint[][]): boolean {
        // Find an empty constraint slot that, if filled, could help solve a cell
        const candidates: { type: 'h' | 'v', r: number, c: number }[] = [];

        // Collect horizontal candidates
        for (let r = 0; r < size; r++) {
            for (let c = 0; c < size - 1; c++) {
                if (hC[r][c] === null) candidates.push({ type: 'h', r, c });
            }
        }
        // Collect vertical candidates
        for (let r = 0; r < size - 1; r++) {
            for (let c = 0; c < size; c++) {
                if (vC[r][c] === null) candidates.push({ type: 'v', r, c });
            }
        }

        // Shuffle candidates to ensure random spatial distribution
        for (let i = candidates.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [candidates[i], candidates[j]] = [candidates[j], candidates[i]];
        }

        for (const cand of candidates) {
            if (cand.type === 'h') {
                hC[cand.r][cand.c] = sol[cand.r][cand.c] === sol[cand.r][cand.c + 1] ? 'equal' : 'not-equal';
                if (this.getHint(grid, sol, size, hC, vC, false)) return true;
                hC[cand.r][cand.c] = null;
            } else {
                vC[cand.r][cand.c] = sol[cand.r][cand.c] === sol[cand.r + 1][cand.c] ? 'equal' : 'not-equal';
                if (this.getHint(grid, sol, size, hC, vC, false)) return true;
                vC[cand.r][cand.c] = null;
            }
        }

        return false;
    }

    private isSolvableByLogic(grid: TangoGrid, sol: ('sun' | 'moon')[][], size: number, hC: Constraint[][], vC: Constraint[][]): boolean {
        const testGrid = grid.map(row => [...row]);
        let changed = true;
        while (changed) {
            const hint = this.getHint(testGrid, sol, size, hC, vC, false);
            if (hint) {
                testGrid[hint.row][hint.col] = hint.symbol;
            } else {
                changed = false;
            }
        }
        return testGrid.every(row => row.every(cell => cell !== null));
    }

    private fillRandomConstraints(h: Constraint[][], v: Constraint[][], sol: ('sun' | 'moon')[][], size: number, count: number) {
        let placed = 0;
        let attempts = 0;
        while (placed < count && attempts < 100) {
            attempts++;
            const isHorizontal = Math.random() > 0.5;
            if (isHorizontal) {
                const r = Math.floor(Math.random() * size);
                const c = Math.floor(Math.random() * (size - 1));
                if (h[r][c] === null) {
                    h[r][c] = sol[r][c] === sol[r][c + 1] ? 'equal' : 'not-equal';
                    placed++;
                }
            } else {
                const r = Math.floor(Math.random() * (size - 1));
                const c = Math.floor(Math.random() * size);
                if (v[r][c] === null) {
                    v[r][c] = sol[r][c] === sol[r + 1][c] ? 'equal' : 'not-equal';
                    placed++;
                }
            }
        }
    }


    private createEmptyGrid(size: number): any[][] {
        return Array.from({ length: size }, () => Array(size).fill(null));
    }

    private solveTango(grid: TangoGrid, size: number): boolean {
        for (let r = 0; r < size; r++) {
            for (let c = 0; c < size; c++) {
                if (grid[r][c] === null) {
                    const symbols: ('sun' | 'moon')[] = Math.random() > 0.5 ? ['sun', 'moon'] : ['moon', 'sun'];
                    for (const sym of symbols) {
                        if (this.isValid(grid, r, c, sym, size)) {
                            grid[r][c] = sym;
                            if (this.solveTango(grid, size)) return true;
                            grid[r][c] = null;
                        }
                    }
                    return false;
                }
            }
        }
        return true;
    }

    isValid(grid: TangoGrid, row: number, col: number, sym: 'sun' | 'moon', size: number): boolean {
        // 1. No three of the same symbols in a row/column
        if (col >= 2 && grid[row][col - 1] === sym && grid[row][col - 2] === sym) return false;
        if (col > 0 && col < size - 1 && grid[row][col - 1] === sym && grid[row][col + 1] === sym) return false;
        if (col <= size - 3 && grid[row][col + 1] === sym && grid[row][col + 2] === sym) return false;

        if (row >= 2 && grid[row - 1][col] === sym && grid[row - 2][col] === sym) return false;
        if (row > 0 && row < size - 1 && grid[row - 1][col] === sym && grid[row + 1][col] === sym) return false;
        if (row <= size - 3 && grid[row + 1][col] === sym && grid[row + 2][col] === sym) return false;

        // 2. Equal number of suns and moons in each row/column
        let rQuota = 0;
        for (let i = 0; i < size; i++) if (grid[row][i] === sym) rQuota++;
        if (rQuota >= size / 2) return false;

        let cQuota = 0;
        for (let i = 0; i < size; i++) if (grid[i][col] === sym) cQuota++;
        if (cQuota >= size / 2) return false;

        return true;
    }

    private getCount(row: TangoSymbol[], sym: 'sun' | 'moon'): number {
        return row.filter(s => s === sym).length;
    }

    private removeCells(grid: TangoGrid, count: number, size: number) {
        let removed = 0;
        while (removed < count) {
            const r = Math.floor(Math.random() * size);
            const c = Math.floor(Math.random() * size);
            if (grid[r][c] !== null) {
                grid[r][c] = null;
                removed++;
            }
        }
    }

    getHint(grid: TangoGrid, solution: ('sun' | 'moon')[][], size: number, hC: Constraint[][], vC: Constraint[][], includeFallback: boolean = true): { row: number, col: number, symbol: 'sun' | 'moon', explanation: string } | null {
        const t = (key: string, params: any = {}) => this.langService.translate(key, params);

        // 1. Regle du Duo
        for (let r = 0; r < size; r++) {
            for (let c = 0; c < size; c++) {
                if (grid[r][c] === null) {
                    const targetSym = solution[r][c];
                    const opp = targetSym === 'sun' ? 'moon' : 'sun';
                    const targetLabel = t(targetSym === 'sun' ? 'sun' : 'moon');
                    const oppLabel = t(opp === 'sun' ? 'sun' : 'moon') + 's';

                    if ((c > 1 && grid[r][c - 1] === opp && grid[r][c - 2] === opp) ||
                        (c < size - 2 && grid[r][c + 1] === opp && grid[r][c + 2] === opp) ||
                        (r > 1 && grid[r - 1][c] === opp && grid[r - 2][c] === opp) ||
                        (r < size - 2 && grid[r + 1][c] === opp && grid[r + 2][c] === opp)) {
                        return { row: r, col: c, symbol: targetSym, explanation: t('tango_hint_duo', { target: targetLabel, opp: oppLabel }) };
                    }
                }
            }
        }

        // 2. Regle du Sandwich
        for (let r = 0; r < size; r++) {
            for (let c = 0; c < size; c++) {
                if (grid[r][c] === null) {
                    const targetSym = solution[r][c];
                    const opp = targetSym === 'sun' ? 'moon' : 'sun';
                    const targetLabel = t(targetSym === 'sun' ? 'sun' : 'moon');
                    const oppLabel = t(opp === 'sun' ? 'sun' : 'moon') + 's';

                    if ((c > 0 && c < size - 1 && grid[r][c - 1] === opp && grid[r][c + 1] === opp) ||
                        (r > 0 && r < size - 1 && grid[r - 1][c] === opp && grid[r + 1][c] === opp)) {
                        return { row: r, col: c, symbol: targetSym, explanation: t('tango_hint_sandwich', { target: targetLabel, opp: oppLabel }) };
                    }
                }
            }
        }

        // 3. Regle du Connecteur Simple
        for (let r = 0; r < size; r++) {
            for (let c = 0; c < size; c++) {
                if (grid[r][c] === null) {
                    const targetSym = solution[r][c];
                    const targetLabel = t(targetSym === 'sun' ? 'sun' : 'moon');

                    // Check horizontal
                    if (c > 0 && grid[r][c - 1] !== null && hC[r][c - 1]) {
                        const sign = hC[r][c - 1];
                        if ((sign === 'equal' && grid[r][c - 1] === targetSym) || (sign === 'not-equal' && grid[r][c - 1] !== targetSym))
                            return { row: r, col: c, symbol: targetSym, explanation: t('tango_hint_connector_same', { target: targetLabel }) };
                    }
                    if (c < size - 1 && grid[r][c + 1] !== null && hC[r][c]) {
                        const sign = hC[r][c];
                        if ((sign === 'equal' && grid[r][c + 1] === targetSym) || (sign === 'not-equal' && grid[r][c + 1] !== targetSym))
                            return { row: r, col: c, symbol: targetSym, explanation: t('tango_hint_connector_same', { target: targetLabel }) };
                    }
                    // Check vertical
                    if (r > 0 && grid[r - 1][c] !== null && vC[r - 1][c]) {
                        const sign = vC[r - 1][c];
                        if ((sign === 'equal' && grid[r - 1][c] === targetSym) || (sign === 'not-equal' && grid[r - 1][c] !== targetSym))
                            return { row: r, col: c, symbol: targetSym, explanation: t('tango_hint_connector_same', { target: targetLabel }) };
                    }
                    if (r < size - 1 && grid[r + 1][c] !== null && vC[r][c]) {
                        const sign = vC[r][c];
                        if ((sign === 'equal' && grid[r + 1][c] === targetSym) || (sign === 'not-equal' && grid[r + 1][c] !== targetSym))
                            return { row: r, col: c, symbol: targetSym, explanation: t('tango_hint_connector_same', { target: targetLabel }) };
                    }
                }
            }
        }

        // 4. Regle du Quota (Ligne/Col)
        for (let r = 0; r < size; r++) {
            const suns = this.getCount(grid[r], 'sun');
            const moons = this.getCount(grid[r], 'moon');
            if (suns === size / 2 || moons === size / 2) {
                const missing = suns === size / 2 ? 'moon' : 'sun';
                const targetLabel = t(missing === 'sun' ? 'sun' : 'moon');
                const oppLabel = t(missing === 'sun' ? 'moon' : 'sun') + 's';
                for (let c = 0; c < size; c++) {
                    if (grid[r][c] === null) return { row: r, col: c, symbol: missing, explanation: t('tango_hint_quota', { type: t('row'), count: size / 2, target: oppLabel, opp: targetLabel }) };
                }
            }
        }
        for (let c = 0; c < size; c++) {
            const col = Array.from({ length: size }, (_, r) => grid[r][c]);
            const suns = this.getCount(col, 'sun');
            const moons = this.getCount(col, 'moon');
            if (suns === size / 2 || moons === size / 2) {
                const missing = suns === size / 2 ? 'moon' : 'sun';
                const targetLabel = t(missing === 'sun' ? 'sun' : 'moon');
                const oppLabel = t(missing === 'sun' ? 'moon' : 'sun') + 's';
                for (let r = 0; r < size; r++) {
                    if (grid[r][c] === null) return { row: r, col: c, symbol: missing, explanation: t('tango_hint_quota', { type: t('col'), count: size / 2, target: oppLabel, opp: targetLabel }) };
                }
            }
        }

        // 5. Regle du Duo-Connecteur
        for (let r = 0; r < size; r++) {
            for (let c = 0; c < size; c++) {
                if (grid[r][c] === null) {
                    const targetSym = solution[r][c];
                    const opp = targetSym === 'sun' ? 'moon' : 'sun';
                    const targetLabel = t(targetSym === 'sun' ? 'sun' : 'moon');
                    const oppLabel = t(opp === 'sun' ? 'sun' : 'moon') + 's';

                    if (c < size - 1 && hC[r][c] === 'equal' && grid[r][c + 1] === null) {
                        if ((c > 0 && grid[r][c - 1] === opp) || (c < size - 2 && grid[r][c + 2] === opp)) {
                            return { row: r, col: c, symbol: targetSym, explanation: t('tango_hint_duo_connector', { target: targetLabel, opp: oppLabel }) };
                        }
                    }
                    if (c > 0 && hC[r][c - 1] === 'equal' && grid[r][c - 1] === null) {
                        if ((c > 1 && grid[r][c - 2] === opp) || (c < size - 1 && grid[r][c + 1] === opp)) {
                            return { row: r, col: c, symbol: targetSym, explanation: t('tango_hint_duo_connector', { target: targetLabel, opp: oppLabel }) };
                        }
                    }
                    // Vertical
                    if (r < size - 1 && vC[r][c] === 'equal' && grid[r + 1][c] === null) {
                        if ((r > 0 && grid[r - 1][c] === opp) || (r < size - 2 && grid[r + 2][c] === opp)) {
                            return { row: r, col: c, symbol: targetSym, explanation: t('tango_hint_duo_connector', { target: targetLabel, opp: oppLabel }) };
                        }
                    }
                    if (r > 0 && vC[r - 1][c] === 'equal' && grid[r - 1][c] === null) {
                        if ((r > 1 && grid[r - 2][c] === opp) || (r < size - 1 && grid[r + 1][c] === opp)) {
                            return { row: r, col: c, symbol: targetSym, explanation: t('tango_hint_duo_connector', { target: targetLabel, opp: oppLabel }) };
                        }
                    }
                }
            }
        }

        // 6. Regle du Quota & Connecteurs 'x'
        for (let r = 0; r < size; r++) {
            const suns = this.getCount(grid[r], 'sun');
            const moons = this.getCount(grid[r], 'moon');
            for (let c = 0; c < size - 1; c++) {
                if (hC[r][c] === 'not-equal' && grid[r][c] === null && grid[r][c + 1] === null) {
                    if (suns + 1 === size / 2) {
                        for (let oc = 0; oc < size; oc++) {
                            if (grid[r][oc] === null && oc !== c && oc !== c + 1)
                                return { row: r, col: oc, symbol: 'moon', explanation: t('tango_hint_connector_diff_quota', { target: t('moon') + 's', opp: t('sun') + 's' }) };
                        }
                    }
                    if (moons + 1 === size / 2) {
                        for (let oc = 0; oc < size; oc++) {
                            if (grid[r][oc] === null && oc !== c && oc !== c + 1)
                                return { row: r, col: oc, symbol: 'sun', explanation: t('tango_hint_connector_diff_quota', { target: t('sun') + 's', opp: t('moon') + 's' }) };
                        }
                    }
                }
            }
        }
        for (let c = 0; c < size; c++) {
            const col = grid.map(r => r[c]);
            const suns = this.getCount(col, 'sun');
            const moons = this.getCount(col, 'moon');
            for (let r = 0; r < size - 1; r++) {
                if (vC[r][c] === 'not-equal' && grid[r][c] === null && grid[r + 1][c] === null) {
                    if (suns + 1 === size / 2) {
                        for (let or = 0; or < size; or++) {
                            if (grid[or][c] === null && or !== r && or !== r + 1)
                                return { row: r, col: c, symbol: 'moon', explanation: t('tango_hint_connector_diff_quota', { target: t('moon') + 's', opp: t('sun') + 's' }) };
                        }
                    }
                    if (moons + 1 === size / 2) {
                        for (let or = 0; or < size; or++) {
                            if (grid[or][c] === null && or !== r && or !== r + 1)
                                return { row: r, col: c, symbol: 'sun', explanation: t('tango_hint_connector_diff_quota', { target: t('sun') + 's', opp: t('moon') + 's' }) };
                        }
                    }
                }
            }
        }

        // 7. Regle de l'Identité du Bord
        for (let r = 0; r < size; r++) {
            if (grid[r][0] !== null && grid[r][0] === grid[r][size - 1]) {
                const sym = grid[r][0];
                const opp = sym === 'sun' ? 'moon' : 'sun';
                const targetLabel = t(opp === 'sun' ? 'sun' : 'moon');
                if (grid[r][1] === null) return { row: r, col: 1, symbol: opp, explanation: t('tango_hint_edge_identity', { target: targetLabel }) };
                if (grid[r][size - 2] === null) return { row: r, col: size - 2, symbol: opp, explanation: t('tango_hint_edge_identity', { target: targetLabel }) };
            }
        }
        for (let c = 0; c < size; c++) {
            const col = grid.map(r => r[c]);
            if (col[0] !== null && col[0] === col[size - 1]) {
                const sym = col[0];
                const opp = sym === 'sun' ? 'moon' : 'sun';
                const targetLabel = t(opp === 'sun' ? 'sun' : 'moon');
                if (grid[1][c] === null) return { row: 1, col: c, symbol: opp, explanation: t('tango_hint_edge_identity', { target: targetLabel }) };
                if (grid[size - 2][c] === null) return { row: size - 2, col: c, symbol: opp, explanation: t('tango_hint_edge_identity', { target: targetLabel }) };
            }
        }

        // 8. Regle des Bords Correspondants
        for (let r = 0; r < size; r++) {
            const row = grid[r];
            if (row[0] !== null && row[0] === row[1] && row[size - 1] === null) {
                const targetSym = solution[r][size - 1];
                return { row: r, col: size - 1, symbol: targetSym, explanation: t('tango_hint_edge_match', { target: t(targetSym === 'sun' ? 'sun' : 'moon'), opp: t(row[0] === 'sun' ? 'sun' : 'moon') + 's' }) };
            }
            if (row[size - 1] !== null && row[size - 1] === row[size - 2] && row[0] === null) {
                const targetSym = solution[r][0];
                return { row: r, col: 0, symbol: targetSym, explanation: t('tango_hint_edge_match', { target: t(targetSym === 'sun' ? 'sun' : 'moon'), opp: t(row[size - 1] === 'sun' ? 'sun' : 'moon') + 's' }) };
            }
        }
        for (let c = 0; c < size; c++) {
            const col = grid.map(r => r[c]);
            if (col[0] !== null && col[0] === col[1] && col[size - 1] === null) {
                const targetSym = solution[size - 1][c];
                return { row: size - 1, col: c, symbol: targetSym, explanation: t('tango_hint_edge_match', { target: t(targetSym === 'sun' ? 'sun' : 'moon'), opp: t(col[0] === 'sun' ? 'sun' : 'moon') + 's' }) };
            }
            if (col[size - 1] !== null && col[size - 1] === col[size - 2] && col[0] === null) {
                const targetSym = solution[0][c];
                return { row: 0, col: c, symbol: targetSym, explanation: t('tango_hint_edge_match', { target: t(targetSym === 'sun' ? 'sun' : 'moon'), opp: t(col[size - 1] === 'sun' ? 'sun' : 'moon') + 's' }) };
            }
        }

        if (!includeFallback) return null;

        // 10. Inférence Avancée (Fallback)
        for (let r = 0; r < size; r++) {
            for (let c = 0; c < size; c++) {
                if (grid[r][c] === null) {
                    const targetSym = solution[r][c];
                    return { row: r, col: c, symbol: targetSym, explanation: t('tango_hint_inference', { target: t(targetSym === 'sun' ? 'sun' : 'moon') }) };
                }
            }
        }

        return null;
    }

    private hasTriple(line: TangoSymbol[]): boolean {
        for (let i = 0; i < line.length - 2; i++) {
            if (line[i] !== null && line[i] === line[i + 1] && line[i + 1] === line[i + 2]) return true;
        }
        return false;
    }

    validateGrid(grid: TangoGrid, size: number, hC: Constraint[][], vC: Constraint[][]): { valid: boolean, errors: { row: number, col: number }[] } {
        const errors: Set<string> = new Set();

        // 1. Check 3-in-a-row
        for (let r = 0; r < size; r++) {
            for (let c = 0; c < size - 2; c++) {
                const s1 = grid[r][c];
                const s2 = grid[r][c + 1];
                const s3 = grid[r][c + 2];
                if (s1 && s1 === s2 && s2 === s3) {
                    errors.add(`${r},${c}`);
                    errors.add(`${r},${c + 1}`);
                    errors.add(`${r},${c + 2}`);
                }
            }
        }
        for (let c = 0; c < size; c++) {
            for (let r = 0; r < size - 2; r++) {
                const s1 = grid[r][c];
                const s2 = grid[r + 1][c];
                const s3 = grid[r + 2][c];
                if (s1 && s1 === s2 && s2 === s3) {
                    errors.add(`${r},${c}`);
                    errors.add(`${r + 1},${c}`);
                    errors.add(`${r + 2},${c}`);
                }
            }
        }

        // 2. Adjacency
        for (let r = 0; r < size; r++) {
            for (let c = 0; c < size - 1; c++) {
                if (hC[r][c] && grid[r][c] && grid[r][c + 1]) {
                    if (hC[r][c] === 'equal' && grid[r][c] !== grid[r][c + 1]) {
                        errors.add(`${r},${c}`); errors.add(`${r},${c + 1}`);
                    }
                    if (hC[r][c] === 'not-equal' && grid[r][c] === grid[r][c + 1]) {
                        errors.add(`${r},${c}`); errors.add(`${r},${c + 1}`);
                    }
                }
            }
        }
        for (let r = 0; r < size - 1; r++) {
            for (let c = 0; c < size; c++) {
                if (vC[r][c] && grid[r][c] && grid[r + 1][c]) {
                    if (vC[r][c] === 'equal' && grid[r][c] !== grid[r + 1][c]) {
                        errors.add(`${r},${c}`); errors.add(`${r + 1},${c}`);
                    }
                    if (vC[r][c] === 'not-equal' && grid[r][c] === grid[r + 1][c]) {
                        errors.add(`${r},${c}`); errors.add(`${r + 1},${c}`);
                    }
                }
            }
        }

        // 3. Counts
        for (let r = 0; r < size; r++) {
            let s = 0, m = 0;
            for (let c = 0; c < size; c++) {
                if (grid[r][c] === 'sun') s++;
                if (grid[r][c] === 'moon') m++;
            }
            if (s > size / 2) { for (let c = 0; c < size; c++) if (grid[r][c] === 'sun') errors.add(`${r},${c}`); }
            if (m > size / 2) { for (let c = 0; c < size; c++) if (grid[r][c] === 'moon') errors.add(`${r},${c}`); }
        }
        for (let c = 0; c < size; c++) {
            let s = 0, m = 0;
            for (let r = 0; r < size; r++) {
                if (grid[r][c] === 'sun') s++;
                if (grid[r][c] === 'moon') m++;
            }
            if (s > size / 2) { for (let r = 0; r < size; r++) if (grid[r][c] === 'sun') errors.add(`${r},${c}`); }
            if (m > size / 2) { for (let r = 0; r < size; r++) if (grid[r][c] === 'moon') errors.add(`${r},${c}`); }
        }

        const full = grid.every(r => r.every(c => c !== null));
        return { valid: full && errors.size === 0, errors: Array.from(errors).map(s => { const [r, c] = s.split(',').map(Number); return { row: r, col: c }; }) };
    }
}
